/**
 * Directory hub — lazy-loads each row's product carousel via the Section
 * Rendering API, mirroring collection-discovery-grid.js.
 *
 * Each `.directory-hub-row[data-collection-handle]` has an SSR header (the
 * child-collection link — the SEO payload) and a skeleton `[data-dh-slot]`.
 * When a row nears the viewport we fetch
 *   /collections/{handle}?section_id={data-section-id}
 * which re-renders the directory-hub section in its SRA branch (section.index
 * is nil, `collection` is the child) → a single resource-list carousel — and
 * we swap that into the slot. No product cards are server-rendered on the hub.
 */

function collectionsBase() {
  if (window.theme && window.theme.routes && window.theme.routes.collections_url) {
    return window.theme.routes.collections_url;
  }
  const m = window.location.pathname.match(/^\/([a-z]{2}(?:-[a-z]{2})?)(?:\/|$)/);
  return m ? `/${m[1]}/collections` : '/collections';
}

const RETRY_DELAY_MS = 2000;

// Resolves true when the row is terminally handled (loaded / genuinely empty /
// gracefully degraded), false on a transient failure that should be retried.
async function loadRow(row) {
  if (row.dataset.dhLoaded && row.dataset.dhLoaded !== 'pending') return true;
  row.dataset.dhLoaded = 'pending';

  const handle = row.dataset.collectionHandle;
  const sectionId = row.dataset.sectionId;
  const slot = row.querySelector('[data-dh-slot]');
  if (!handle || !sectionId || !slot) {
    row.dataset.dhLoaded = 'nofrag';
    return true;
  }

  try {
    const url = new URL(`${collectionsBase()}/${handle}`, window.location.origin);
    url.searchParams.set('section_id', sectionId);

    const res = await fetch(url.toString(), { headers: { 'X-Requested-With': 'fetch' } });
    if (!res.ok) throw new Error(`SRA ${res.status}`);

    const html = await res.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    // The SRA fragment is the resource-list carousel wrapper (directory-hub-carousel).
    const carousel =
      doc.querySelector('.directory-hub-row__list') ||
      doc.querySelector('.resource-list__carousel') ||
      doc.querySelector('.resource-list');

    if (carousel) {
      slot.replaceChildren(carousel); // moving nodes into the live doc upgrades <slideshow-*> / <mock-image>
      row.dataset.dhLoaded = '1';
      return true;
    }

    // 200 but no carousel. Distinguish two cases so we never silently drop a row
    // on a section-id resolution failure:
    //   • a full HTML page came back (section_id didn't resolve to our section) —
    //     degrade gracefully: keep the SSR header + child link (the SEO payload),
    //     just stop the skeleton shimmer.
    //   • an empty section fragment — the child genuinely has no products — drop it.
    const isFullPage = doc.querySelector('head > title, body > header, #MainContent, main');
    if (isFullPage) {
      const skeleton = row.querySelector('.directory-hub-row__skeleton');
      if (skeleton) skeleton.remove();
      row.dataset.dhLoaded = 'nofrag';
    } else {
      row.remove();
    }
    return true;
  } catch (e) {
    row.dataset.dhLoaded = ''; // transient — allow a retry (see re-observe below)
    return false;
  }
}

function init() {
  const rows = document.querySelectorAll('.directory-hub-row[data-collection-handle]');
  if (!rows.length) return;

  if ('IntersectionObserver' in window) {
    const io = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          obs.unobserve(entry.target); // avoid overlapping loads for the same row
          loadRow(entry.target).then((done) => {
            // On a transient failure, re-observe so it retries — observe() re-fires
            // the callback if the row is still on screen. (Unlike unobserve-then-
            // reset, which would leave the row stuck forever.)
            if (!done && entry.target.isConnected) {
              setTimeout(() => obs.observe(entry.target), RETRY_DELAY_MS);
            }
          });
        });
      },
      { rootMargin: '600px' } // begin loading well before the row scrolls in
    );
    rows.forEach((row) => io.observe(row));
  } else {
    rows.forEach(loadRow);
  }
}

if (document.readyState !== 'loading') {
  init();
} else {
  document.addEventListener('DOMContentLoaded', init);
}
